import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import moment from "moment-timezone";
import { toast } from "react-toastify";

import Topbar from "../../components/topbar/topbar";
import Footer from "../client/appointment/footer";
import ClientForm from "../guestbooking/clientform";
import TimezoneGate from "./timezoneGate";
import EventList from "./eventList";
import Confirmation from "./confirmation";

import { setLoading } from "../../reducer/auth/auth";
import { createGuestBooking } from "../../services/client";
import { getCountry, getLanguages, getTimezones } from "../../services/uidata";
import { filterVisibleTimeZones } from "../../utils/helpers";

import "../guestbooking/booking.css";
import "./specialevents.css";

const TZ_STORAGE_KEY = "special_events_tz";

// Defensive fallback only — the backend always returns each event's real
// per-medium payment_methods (see GuestSpecialEventController), this is just
// what to show if that field is ever missing (e.g. a stale mock).
const DEFAULT_PAYMENT_INFO = {
  is_paypal_integrated: false,
  is_squareup_integrated: false,
  is_stripe_integrated: false,
};

const eventMoment = (event, time) =>
  moment(`${event.date} ${time}`, ["YYYY-MM-DD HH:mm", "YYYY-MM-DD hh:mm A"]);

export default function SpecialEvents() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const mediumId = searchParams.get("medium_id");
  const availabilityId = searchParams.get("availability_id");

  // steps: timezone → events → form → confirm
  const [step, setStep] = useState("timezone");
  const [selection, setSelection] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [counterStatus, setCounterStatus] = useState(false);

  const [timezoneData, setTimezoneData] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [formLanguages, setFormLanguages] = useState([]);

  useEffect(() => {
    // same clean-up the existing guest booking flow does on entry
    localStorage.removeItem("guest_id");

    getCountry()
      .then((data) => {
        if (data.data && data.data.data) setCountryData(data.data.data);
      })
      .catch((err) => console.log(err));

    getTimezones()
      .then((data) => {
        if (!data.data || !data.data.data) return;
        const all = data.data.data;
        const items = filterVisibleTimeZones(all);
        const guess = all.find((item) => item.name === moment.tz.guess());
        if (guess && !items.find((item) => item.id === guess.id)) {
          items.push(guess);
        }
        setTimezoneData(items);
      })
      .catch((err) => console.log(err));

    getLanguages()
      .then((data) => {
        if (data.data && data.data.data) setFormLanguages(data.data.data);
      })
      .catch((err) => console.log(err));

    // Refresh skips the gate when a timezone was already chosen this session
    try {
      const stored = JSON.parse(sessionStorage.getItem(TZ_STORAGE_KEY));
      if (stored && stored.timeZoneValue && stored.countryValue) {
        setSelection(stored);
        setStep("events");
      }
    } catch (err) {
      sessionStorage.removeItem(TZ_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [step]);

  const handleGateNext = (chosen) => {
    setSelection(chosen);
    sessionStorage.setItem(TZ_STORAGE_KEY, JSON.stringify(chosen));
    setStep("events");
  };

  const handleBook = (event) => {
    setSelectedEvent(event);
    setStep("form");
  };

  const eventSummary = (event) => {
    const price =
      Number(event.price) > 0
        ? `$${Number(event.price)} ${event.currency || "USD"}`
        : "Free";
    return `Pro Demo: ${event.title} — ${price}`;
  };

  // Called by the reused guestbooking ClientForm on submit
  const createBooking = (info) => {
    const event = selectedEvent;
    if (!event) return;
    const start = eventMoment(event, event.start_time);
    const end = eventMoment(event, event.end_time);
    const body = {
      first_name: info.first_name,
      last_name: info.last_name,
      email: info.email,
      phone: info.phone,
      whatsapp: info.whatsapp,
      timezone: selection.timeZoneValue,
      date: event.date,
      time: start.format("hh:mm A"),
      session_id: event.session_id,
      subsession_id: event.subsession_id,
      medium_id: event.medium_id,
      // special-events specific fields (backend books the exact occurrence
      // and enforces capacity)
      medium_availability_id: event.medium_availability_id,
      event_id: event.event_id,
      agree_to_legal_terms_and_conditionss: info.agree_to_legal_terms_and_conditionss,
      opt_in: info.opt_in,
      amount: event.price,
      duration: end.diff(start, "minutes"),
      method: info.paymenttype,
      currency: event.currency,
      appointment_type: "paybefore",
      country: info.country,
      languages: info.languages,
      // medium-level Pay It Forward flag (from the listing API) — routes the
      // charge through the central VerySoul account, same as /guest-booking
      payItforward: event.medium_pay_forward ? 1 : 0,
    };

    dispatch(setLoading(true));
    createGuestBooking(body)
      .then((data) => {
        dispatch(setLoading(false));
        if (data && data.data && data.data.status) {
          const result = data.data.data || {};
          setUserDetails(body);
          if (result.approvedUrl) {
            // paid event — redirect to PayPal/Square checkout
            setCounterStatus(true);
            window.open(result.approvedUrl, "_self");
          } else {
            // free event — confirm right away
            setStep("confirm");
          }
        } else if (data && data.data && data.data.message) {
          toast.error(data.data.message, { position: "top-center" });
        } else {
          toast.error("Unable to complete your booking. Please try again.", {
            position: "top-center",
          });
        }
      })
      .catch(() => {
        dispatch(setLoading(false));
        toast.error("Unable to complete your booking. Please try again.", {
          position: "top-center",
        });
      });
  };

  return (
    <div className="new-appointment new-appointment-listt special-events-page pb-5">
      <Topbar />
      {step === "timezone" && (
        <TimezoneGate
          countryData={countryData}
          timezoneData={timezoneData}
          selection={selection}
          onNext={handleGateNext}
        />
      )}
      {step === "events" && (
        <EventList
          selection={selection}
          onChangeTimezone={() => setStep("timezone")}
          onBook={handleBook}
          mediumId={mediumId}
          availabilityId={availabilityId}
        />
      )}
      {step === "form" && selectedEvent && (
        <ClientForm
          createBooking={createBooking}
          bookingDate={selectedEvent.date}
          bookingTime={eventMoment(selectedEvent, selectedEvent.start_time).format("hh:mm A")}
          timeZoneName={selection.timeZoneName}
          paymentInfo={selectedEvent.payment_methods || DEFAULT_PAYMENT_INFO}
          payItForward={selectedEvent.medium_pay_forward ? 1 : 0}
          session_id={selectedEvent.session_id}
          subsession_id={selectedEvent.subsession_id}
          medium_id={selectedEvent.medium_id}
          price={selectedEvent.price}
          duration={eventMoment(selectedEvent, selectedEvent.end_time).diff(
            eventMoment(selectedEvent, selectedEvent.start_time),
            "minutes"
          )}
          setCounterStatus={setCounterStatus}
          counterStatus={counterStatus}
          formLanguages={formLanguages}
          countryData={countryData}
          countryName={selection.countryName}
          userCountryCode={selection.countryCode || "US"}
          eventSummary={eventSummary(selectedEvent)}
        />
      )}
      {step === "confirm" && (
        <Confirmation
          event={selectedEvent}
          userDetails={userDetails}
          timeZoneName={selection.timeZoneName}
        />
      )}
      <div className="text-center pb-5 mt-5 pt-5 padding-botton-120px">
        <Footer />
      </div>
    </div>
  );
}
